import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  EyeOff,
  Check,
  Loader2,
  Settings,
  Zap,
  CreditCard,
  BarChart3,
  ExternalLink,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  connected: boolean;
  apiKeyPlaceholder: string;
  docsUrl: string;
}

const integrations: Integration[] = [
  {
    id: "grok",
    name: "Grok AI",
    description: "AI-powered content generation and analysis",
    icon: Zap,
    iconColor: "text-blue-500",
    iconBg: "bg-blue-500/10",
    connected: false,
    apiKeyPlaceholder: "xai-xxxxxxxxxxxxxxxxxxxxxxxx",
    docsUrl: "https://x.ai/api",
  },
  {
    id: "stripe",
    name: "Stripe",
    description: "Payment processing and subscription management",
    icon: CreditCard,
    iconColor: "text-violet-500",
    iconBg: "bg-violet-500/10",
    connected: true,
    apiKeyPlaceholder: "sk_live_xxxxxxxxxxxxxxxxxxxxxxxx",
    docsUrl: "https://stripe.com/docs/api",
  },
  {
    id: "ga",
    name: "Google Analytics",
    description: "Website traffic and user behavior tracking",
    icon: BarChart3,
    iconColor: "text-amber-500",
    iconBg: "bg-amber-500/10",
    connected: false,
    apiKeyPlaceholder: "G-XXXXXXXXXX",
    docsUrl: "https://analytics.google.com",
  },
];

function IntegrationCard({
  integration,
  onConfigure,
}: {
  integration: Integration;
  onConfigure: (id: string) => void;
}) {
  const Icon = integration.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="relative overflow-hidden group">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className={cn("p-3 rounded-xl", integration.iconBg)}>
                <Icon className={cn("w-6 h-6", integration.iconColor)} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{integration.name}</h3>
                  <Badge
                    variant={integration.connected ? "default" : "secondary"}
                    className={cn(
                      "text-xs",
                      integration.connected
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {integration.connected ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Connected
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Not connected
                      </span>
                    )}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {integration.description}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onConfigure(integration.id)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Configure
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a
                href={integration.docsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Docs
              </a>
            </Button>
          </div>
        </CardContent>

        {/* Subtle gradient on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      </Card>
    </motion.div>
  );
}

function IntegrationConfigModal({
  integration,
  isOpen,
  onClose,
  onSave,
}: {
  integration: Integration | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, apiKey: string) => void;
}) {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  if (!integration || !isOpen) return null;

  const Icon = integration.icon;

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    onSave(integration.id, apiKey);
    setIsSaving(false);
    setApiKey("");
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-background border border-border rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", integration.iconBg)}>
              <Icon className={cn("w-5 h-5", integration.iconColor)} />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Configure {integration.name}</h2>
              <p className="text-sm text-muted-foreground">
                Enter your API key to connect
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="integrationApiKey">API Key</Label>
            <div className="relative">
              <Input
                id="integrationApiKey"
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={integration.apiKeyPlaceholder}
                className="pr-10 font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showKey ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Your API key is encrypted and stored securely
            </p>
          </div>
        </div>

        <div className="p-6 pt-0 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!apiKey || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              "Connect"
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function IntegrationSettings() {
  const [integrationsState, setIntegrationsState] = useState(integrations);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const currentIntegration = integrationsState.find((i) => i.id === configuring);

  const handleConfigure = (id: string) => {
    setConfiguring(id);
  };

  const handleSave = (id: string, _apiKey: string) => {
    setIntegrationsState((prev) =>
      prev.map((i) => (i.id === id ? { ...i, connected: true } : i))
    );
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Settings className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Third-Party Integrations</h2>
                <p className="text-sm text-muted-foreground">
                  Connect external services to enhance your admin panel capabilities
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {integrationsState.map((integration, index) => (
          <motion.div
            key={integration.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <IntegrationCard
              integration={integration}
              onConfigure={handleConfigure}
            />
          </motion.div>
        ))}
      </div>

      {/* Status Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Integration Status</CardTitle>
            <CardDescription>
              Overview of your connected services
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm">
                  {integrationsState.filter((i) => i.connected).length} Connected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-muted-foreground/30" />
                <span className="text-sm">
                  {integrationsState.filter((i) => !i.connected).length} Not connected
                </span>
              </div>
            </div>

            {saved && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              >
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Integration connected successfully!</span>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Configuration Modal */}
      <IntegrationConfigModal
        integration={currentIntegration || null}
        isOpen={!!configuring}
        onClose={() => setConfiguring(null)}
        onSave={handleSave}
      />
    </div>
  );
}
